import { recurrenceExclusionDate } from 'utils/recurrenceExclusions'
import './styles.scss'

import { Box, darken, lighten, Radio, useTheme } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { ArchiveIcon, TaskEmail, TaskGift } from 'assets/icons'
import { ArrowRightGrey } from 'assets/img'
import IconButton from 'components/buttons/IconButton'
import dateToTimeString from 'components/calendar/components'
import { isInstanceCompleted } from 'components/calendar/components/Event'
import { useEventPopoverCallbacks } from 'components/calendar/eventPopover/EventPopoverProvider'
import { LocationLabel } from 'components/calendar/styled'
import EventContextMenu from 'components/contextMenu/EventContextMenu'
import { EventPopupContent } from 'components/popups/eventInfo/EventPopup'
import { useAppUIType } from 'components/providers/AppUITypeProvider'
import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import React, {
  ComponentType,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import { updateEventOnServer } from 'services/api/event'
import ObserveService from 'services/db/Observe'
import { localEventById, RemoveTempEvents } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  MasterLabelDoc,
  TaskType,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { CALENDAR_ITEM_CORNER_RARIUS } from 'utils/constants'
import { eventIdForContainer, provideEventTitle } from 'utils/event'
import { labelText } from 'utils/label'
import {
  convertLabelColorToStringColor,
  defaultTaskColor,
  isLabelInsideMasterLabel,
  provideTaskColor
} from 'utils/masterLabel'
import { isGuest } from 'utils/user'

import IconsForEvent from './IconsForEvent'
import SomedayItem from './SomedayItem'

interface ItemProps {
  item: EventDoc
  withToggle?: boolean
  masterLabel?: Model<MasterLabelDoc>
  account?: Model<AccountDoc>
  userSettings?: Model<UserSettingsDoc>
  groups?: Model<GroupDoc>[]
  setShowEvent: (id: string) => void
  open: boolean
  droppableId: string
  setOpen?: (b: boolean) => void
  hideTime?: boolean
  instanceStart?: number
  instanceEnd?: number
  onClose?: () => void
  title?: string
}

const Item: React.FC<ItemProps> = ({
  item,
  withToggle,
  masterLabel,
  setShowEvent,
  account,
  userSettings,
  open,
  setOpen,
  droppableId,
  hideTime,
  instanceStart,
  instanceEnd,
  onClose,
  title,
  groups
}) => {
  const theme = useTheme()
  const appUiTypeContext = useAppUIType()
  const [processing, setProcessing] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [selectedEventForMenu, setSelectedEventForMenu] = useState<{
    event: EventDoc
    instanceStart: number
    instanceEnd: number
  } | null>(null)

  function updateItemWithStatus(newState: EventStatus) {
    if (item.Recurrence && typeof item.Recurrence !== 'string') {
      const exclusionDate = recurrenceExclusionDate(
        Number(item.StartDate) * 1000,
        item
      )
      const exDate = exclusionDate.Date

      localEventById(item._id ?? '').then((event) => {
        updateEventOnServer(event, {
          Recurrence: {
            ...item.Recurrence,
            Exclusion: [
              {
                Date: exDate ?? '',
                Status: newState,
                ExclusionDate: exclusionDate.ExclusionDate
              },
              ...(item.Recurrence?.Exclusion &&
              typeof item.Recurrence?.Exclusion !== 'string'
                ? item.Recurrence.Exclusion
                : [])
            ]
              .filter(
                (it, index, self) =>
                  index ===
                  self.findIndex((i) => {
                    const dayjsExclusionI = dayjs(
                      Number(i.Date) * 1000
                    ).startOf('day')
                    const dayjsExclusionIt = dayjs(
                      Number(it.Date) * 1000
                    ).startOf('day')

                    return (
                      dayjsExclusionI.format('YYYY-MM-DD') ===
                      dayjsExclusionIt.format('YYYY-MM-DD')
                    )
                  })
              )
              .filter((exclusion) => {
                return exclusion.Status !== EventStatus.ACTIVE
              })
          },
          SupplementaryGroupsIDs: item.SupplementaryGroupsIDs
        })
      })
    } else {
      updateEventOnServer(item, {
        Status: newState,
        SupplementaryGroupsIDs: item.SupplementaryGroupsIDs
      })
    }
  }

  const [toggleState, setToggleState] = useState(
    item.Status === EventStatus.COMPLETED ||
      isInstanceCompleted(item, Number(item.StartDate) * 1000)
  )

  useEffect(() => {
    setToggleState(
      item.Status === EventStatus.COMPLETED ||
        isInstanceCompleted(item, Number(item.StartDate) * 1000)
    )
  }, [item._rev, item.Recurrence?.Exclusion])

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const newState = toggleState ? EventStatus.ACTIVE : EventStatus.COMPLETED
    setToggleState(newState === EventStatus.COMPLETED)
    setProcessing(true)
    updateItemWithStatus(newState)
  }

  const handleContextMenu = (
    event: React.MouseEvent,
    eventDoc: EventDoc,
    instanceStart: number,
    instanceEnd: number
  ) => {
    event.preventDefault()
    setContextMenuPosition({ x: event.clientX, y: event.clientY })
    setSelectedEventForMenu({
      event: eventDoc,
      instanceStart,
      instanceEnd
    })
  }

  const handleCloseContextMenu = () => {
    setContextMenuPosition(null)
    setSelectedEventForMenu(null)
  }

  const taskItemAction = () => {
    const contact = item.ContactDetails
    const hostName = account?.Name?.FullName ?? account?.Name?.FirstName ?? ''
    const guestName = contact?.Name
    const email = contact?.data?.[0].Email

    if (item.Status === EventStatus.COMPLETED) {
      updateItemWithStatus(EventStatus.REMOVED)
    }

    if (item.TaskType === TaskType.gift) {
      appUiTypeContext.setGiftLink(
        `https://giftit.jifiti.com/gifting.html?host_name=${guestName}&guest_email=${LocalStorage.get(
          LocalStorageKey.USER_EMAIL
        )}&guest_name=${hostName}`
      )
    }

    if (item.TaskType === TaskType.email) {
      window.open(
        `mailto:${email}?body=${
          (item.Notes?.length ?? 0) > 0 && typeof item.Notes !== 'string'
            ? item.Notes?.[0].Note
            : ''
        }`
      )
    }
  }

  const { openPopover, closePopover } = useEventPopoverCallbacks()

  const lText = labelText(
    masterLabel?.Labels.find(
      (mLabel) =>
        mLabel.ID ===
        (appUiTypeContext.selectedLabel
          ? appUiTypeContext.selectedLabel.ID
          : typeof item.Label !== 'string' && (item.Label?.length ?? 0) > 0
            ? item.Label?.filter((label) =>
                isLabelInsideMasterLabel(masterLabel, label.LabelText)
              )?.[0]?.LabelText
            : undefined)
    )
  )

  let lineColor = appUiTypeContext.selectedLabel
    ? convertLabelColorToStringColor(
        masterLabel?.Labels.find(
          (mLabel) => mLabel.ID === appUiTypeContext.selectedLabel?.ID
        )?.Color ?? defaultTaskColor
      )
    : provideTaskColor(masterLabel, item, account)

  if (highestActiveTier(account) === Tier.FREE) lineColor = defaultTaskColor

  let startTime = dayjs(Number(item.StartDate) * 1000)
  let endTime = dayjs(Number(item.EndDate) * 1000)

  const taskTypeIcon = makeTaskTypeIcon(item, toggleState)

  const backgroundColor =
    lineColor && lineColor != defaultTaskColor
      ? theme.palette.mode === 'dark'
        ? darken(lineColor, 0.72)
        : lighten(lineColor, 0.95)
      : theme.palette.mode === 'dark'
        ? 'var(--color-surface-hover)'
        : 'var(--color-surface-card)'
  const itemBorderColor =
    theme.palette.mode === 'dark' ? undefined : theme.palette.text.primary
  let dateText =
    startTime.isBefore(dayjs().startOf('day')) ||
    startTime.isAfter(dayjs().add(1, 'day').endOf('day'))
      ? `${dayjs(Number(item.StartDate) * 1000)
          .tz(getDefaultTimezone())
          .format('MMM D YYYY')}`
      : ''

  if (item.AllDay === '1' && dateText === '') {
    dateText = t('allDay')
  }

  if (item.late) {
    startTime = dayjs(Number(item.lateOriginalStartTime) * 1000)
    endTime = dayjs(Number(item.lateOriginalEndTime) * 1000)
  }

  const startText = dateToTimeString(startTime.toDate(), true)
  const endText = dateToTimeString(endTime.toDate(), true)

  const icons = <IconsForEvent instanceEnd={instanceEnd} item={item} />
  let location: string | undefined = undefined

  if (item.Location && item.Location?.Address) {
    location = item.Location.Address
  }

  const eventRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTimeout(() => {
      const storedData = LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA)
      let pendingData: EventDoc | undefined = undefined
      if (storedData) {
        pendingData = JSON.parse(storedData)
      }
      if (
        (item._id?.length === 0 ||
          (pendingData && pendingData.id === item.id)) &&
        eventRef &&
        LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) !== '1'
      ) {
        eventRef?.current?.click()
      }
    }, 50)
  }, [eventRef, open])

  return (
    <>
      {contextMenuPosition && selectedEventForMenu && (
        <EventContextMenu
          x={contextMenuPosition.x}
          y={contextMenuPosition.y}
          onClose={handleCloseContextMenu}
          event={selectedEventForMenu.event}
          group={undefined}
          instanceStart={selectedEventForMenu.instanceStart * 1000}
          instanceEnd={selectedEventForMenu.instanceEnd * 1000}
        />
      )}
      <div
        key={item._rev ?? item.id ?? item._id}
        id={eventIdForContainer(item, (Number(item.StartDate) ?? 0) * 1000)}
        data-status={item.Status}
        onContextMenu={(mouseEvent) => {
          if (item._id?.length === 0 || isGuest()) return
          handleContextMenu(
            mouseEvent,
            item,
            instanceStart ?? Number(item.StartDate),
            instanceEnd ?? Number(item.EndDate)
          )
        }}
      >
        <Box
          onClick={(e) => {
            e.stopPropagation()
            if (item.someday === '1') {
              // handle late task click here
            } else {
              openPopover(
                eventIdForContainer(
                  item,
                  (Number(item.StartDate) ?? 0) * 1000
                ) ??
                  item.id ??
                  item._id ??
                  '',
                <EventPopupContent
                  awayClickTriggered={false}
                  setAwayClickTriggered={() => {}}
                  account={account!}
                  groups={groups}
                  userSettings={userSettings!}
                  event={item}
                  open={true}
                  group={undefined}
                  instanceStart={
                    item.late
                      ? startTime.valueOf()
                      : (instanceStart ?? startTime.valueOf())
                  }
                  instanceEnd={
                    item.late
                      ? endTime.valueOf()
                      : (instanceEnd ?? endTime.valueOf())
                  }
                  onClose={() => {
                    closePopover()
                    RemoveTempEvents('Close in Item')
                  }}
                  fc_id={item._id ?? ''}
                  showSmallUI={isGuest()}
                />,
                false,
                undefined,
                (e.currentTarget.parentElement as HTMLElement) ??
                  e.currentTarget
              )
            }
          }}
          ref={eventRef}
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            maxHeight: '40px',
            width: '100%',
            minHeight: '46px',
            alignItems: 'center',
            borderRadius: CALENDAR_ITEM_CORNER_RARIUS,
            overflow: 'hidden',
            border: `1px solid ${itemBorderColor}`,
            backgroundColor: backgroundColor,
            opacity:
              processing &&
              `${item._id}${item.StartDate}` ===
                LocalStorage.get(LocalStorageKey.FOCUS_EVENT)
                ? 0.2
                : 1,
            transition: 'opacity 250ms ease-in-out'
          }}
        >
          <Box
            sx={{
              width: 6,
              position: 'absolute',
              height: '100%',
              backgroundColor: lineColor
            }}
          />

          {withToggle && item.someday !== '1' && (
            <Box
              sx={{
                marginLeft: theme.spacing(0, 2),
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Radio
                color="primary"
                checked={toggleState}
                onClick={handleToggle}
              />
            </Box>
          )}
          <div style={{ width: '100%' }}>
            <Box
              sx={{
                flex: 1,
                flexDirection: 'column',
                display: 'flex',
                overflow: 'hidden',
                padding: theme.spacing(0, withToggle ? 0 : 4)
              }}
            >
              {item.someday === '1' ? (
                <></>
              ) : (
                <Box className="time" flexDirection={'row'}>
                  {icons}
                  {!hideTime ? dateText : ''}
                  {item.AllDay === '1' || hideTime
                    ? ''
                    : `${dateText ? ', ' : ''}${startText}${
                        startText !== endText ? '-' + endText : ''
                      }`}
                  {lText && (
                    <Box
                      className="time"
                      style={{
                        padding: theme.spacing(0.5, 1),

                        marginLeft: 3,

                        border: 'solid 1px var(--color-border-default)',
                        borderRadius: '0.25rem',
                        lineHeight: '13px',
                        fontSize: '11px'
                      }}
                    >
                      {lText.toUpperCase()}
                    </Box>
                  )}
                </Box>
              )}
              <div
                className="subTitle"
                style={{
                  color:
                    item.Priority === '2'
                      ? 'var(--color-negative)'
                      : theme.palette.text.primary,
                  textDecoration: toggleState ? 'line-through' : 'none',
                  position: 'relative'
                }}
              >
                {item.someday === '1' ? (
                  <SomedayItem withArrow title={item.Text} />
                ) : item.Text?.length === 0 && item._id?.length === 0 ? (
                  item.Type === DocType.TASK ? (
                    t('newTask')
                  ) : (
                    t('newNote')
                  )
                ) : (
                  <Box display={'flex'} flexDirection={'row'} gap={1}>
                    {`${title ?? provideEventTitle(item)}${location ? ',' : ''}`}
                    <LocationLabel>
                      {location ? ` ${location}` : ''}
                    </LocationLabel>
                  </Box>
                )}
              </div>
            </Box>
          </div>
          {taskTypeIcon && (
            <div
              style={{
                position: 'absolute',
                right: i18n.dir() === 'ltr' ? 0 : undefined,
                left: i18n.dir() === 'ltr' ? undefined : 0,
                marginRight: i18n.dir() === 'ltr' ? 0 : undefined,
                marginLeft: i18n.dir() === 'ltr' ? undefined : 0,
                background: backgroundColor,
                height: '100%',
                justifyItems: 'center',
                display: 'flex'
              }}
            >
              <IconButton
                stopPropagationOnClick
                onClick={taskItemAction}
                size="md"
              >
                {taskTypeIcon}
              </IconButton>
            </div>
          )}
        </Box>
      </div>
    </>
  )
}

export default withObservables([], () => ({
  masterLabel: ObserveService.MasterLabel(),
  account: ObserveService.Account(),
  groups: ObserveService.Groups()
}))(Item as ComponentType<unknown>) as any

function makeTaskTypeIcon(
  item: Model<EventDoc> | EventDoc,
  toggleState: boolean
): ReactNode | undefined {
  const taskType = item.TaskType
  const iconHeight = 30

  if (item.Status === EventStatus.COMPLETED || toggleState)
    return <ArchiveIcon height={iconHeight} />

  if (!taskType) return undefined

  if (taskType) {
    switch (taskType) {
      case TaskType.call:
        return undefined
      case TaskType.text:
        return undefined
      case TaskType.email:
        return <TaskEmail height={iconHeight} />
      case TaskType.gift:
        return <TaskGift height={iconHeight} />
      case TaskType.meet:
        return undefined
      case TaskType.hotel:
        return undefined
      case TaskType.googleTask:
        return undefined
    }
  }

  return undefined
}
